<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import type { SbImage } from '../types';
	import Blok from '../blok.svelte';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Image from '../components/image.svelte';
	import { editable } from '../editable';
	import SbRichtext from '../components/sbRichtext.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import FullscreenVideo from '$lib/fullscreenVideo.svelte';
	import { device } from '$lib/state/device.svelte';
	interface Props {
		content: ISbStoryData<{
			headline: ISbRichtext;
			headline_style: 'bold' | 'regular';
			subheadline: ISbRichtext;
			buttons: ISbStoryData[];
			fullscreen_video: ISbStoryData['content'];
			fullscreen_button_label: string;
			background_asset_mobile: ISbStoryData[];
			background_asset_mobile_bottom?: ISbStoryData[];
			background_asset_desktop: ISbStoryData[];
			background_asset_desktop_bottom?: ISbStoryData[];
			background_color?: 'white' | 'dark-green' | 'light-green';
			text_alignment_mobile: 'top' | 'bottom';
			text_alignment_desktop: 'left-center' | 'left-bottom' | 'right-center' | 'right-bottom';
			headline_asset_mobile?: SbImage;
			headline_asset_mobile_size?: 'span-2' | 'span-4';
			headline_asset_desktop?: SbImage;
			headline_asset_desktop_position?: 'left' | 'right';
			headline_asset_desktop_size?: 'span-4' | 'span-5' | 'span-7';
			press_banner?: ISbStoryData[];
		}>['content'];
	}

	let { content }: Props = $props();

	let asset = $derived(
		device.data.isDesktop
			? content.background_asset_desktop?.[0] || content.background_asset_mobile?.[0]
			: content.background_asset_mobile?.[0]
	);

	let bottomAsset = $derived(
		device.data.isDesktop
			? content.background_asset_desktop_bottom?.[0] || content.background_asset_mobile_bottom?.[0]
			: content.background_asset_mobile_bottom?.[0]
	);

	let fullscreenVideoPlaybackId = $derived(
		device.data.isDesktop
			? content.fullscreen_video?.[0]?.asset_desktop?.[0]?.playback_id ||
					content.fullscreen_video?.[0]?.asset_mobile?.[0]?.playback_id
			: content.fullscreen_video?.[0]?.asset_mobile?.[0]?.playback_id
	);

	let headlineAsset = $derived(
		device.data.isDesktop ? content.headline_asset_desktop : content.headline_asset_mobile
	);

	let backgroundPosition: 'left' | 'right' = $derived(
		['left-center', 'left-bottom'].includes(content.text_alignment_desktop) ||
			(headlineAsset?.filename && content.headline_asset_desktop_position === 'left')
			? 'right'
			: 'left'
	);

	let expandedVideo = $state(false);
</script>

<section
	id="hero"
	class={[
		`bg-${content.background_color || 'white'} ${content.background_color === 'white' ? 'hero-bg-light' : 'hero-bg-dark'} relative flex`,
		['green-1', 'green-2'].includes(content.background_color || '') ? 'text-white' : 'text-brown-1',
		content.text_alignment_mobile === 'top' && 'flex-col-reverse',
		content.text_alignment_mobile === 'bottom' && 'flex-col',
		content.text_alignment_mobile === 'bottom' && bottomAsset && 'mobile-header-padding'
	]}
	use:editable={content}
>
	{#if asset}
		<div
			class={[
				'desktop-header-margin flex flex-1 gap-x-2 gap-y-5 overflow-hidden lg:absolute lg:inset-y-0 lg:mx-5 lg:mb-5 lg:w-[calc(50%-20px)] lg:flex-col ',
				backgroundPosition === 'right' && 'lg:right-0',
				backgroundPosition === 'left' && 'lg:left-0',
				bottomAsset && 'max-lg:px-5',
				bottomAsset && content.text_alignment_mobile === 'top' && 'max-lg:pb-6',
				(content.press_banner?.length || 0) > 0 && 'max-lg:aspect-3/2'
			]}
		>
			<div class={['flex-1 overflow-hidden lg:rounded-xl', bottomAsset && 'max-lg:rounded-xl']}>
				<Blok content={asset} />
			</div>
			{#if bottomAsset}
				<div class="flex-1 overflow-hidden rounded-xl">
					<Blok content={bottomAsset} />
				</div>
			{/if}
		</div>
	{/if}

	<div
		class={[
			'flex flex-1 flex-col justify-center',
			{
				'lg:justify-center': ['left-center', 'right-center'].includes(
					content.text_alignment_desktop
				)
			},
			{
				'lg:justify-end lg:pb-[88px]': ['left-bottom', 'right-bottom'].includes(
					content.text_alignment_desktop
				)
			}
		]}
	>
		<PageGrid16 margin>
			<div
				class={[
					'col-span-full max-lg:pb-6 max-lg:pt-7',
					{
						'lg:col-span-6 lg:col-start-2':
							['left-center', 'left-bottom'].includes(content.text_alignment_desktop) &&
							!headlineAsset?.filename
					},
					{
						'lg:col-span-6 lg:col-start-10':
							['right-center', 'right-bottom'].includes(content.text_alignment_desktop) &&
							!headlineAsset?.filename
					},
					headlineAsset?.filename && {
						[`lg:col-${content.headline_asset_desktop_size}`]: true,
						'lg:col-start-10': content.headline_asset_desktop_position === 'right',
						'lg:col-start-1': content.headline_asset_desktop_position === 'left'
					},
					content.text_alignment_mobile === 'top' && 'mobile-header-padding'
				]}
			>
				<div
					class={[
						`text-balance ${content.press_banner?.length || 0 > 0 ? 'text-[44px]/[108%] tracking-[-.44px]' : 'text-[52px]/[108%] tracking-[-1.04px]'} `,
						{
							'font-semibold lg:text-[80px]/[100%] lg:tracking-[-1.6px]':
								content.headline_style === 'bold'
						},
						{
							'lg:text-[96px]/[100%] lg:tracking-[-1.92px]': content.headline_style === 'regular'
						}
					]}
				>
					{#if headlineAsset?.filename}
						<Image
							class={[
								'w-full',
								content.headline_asset_mobile_size === 'span-2'
									? 'max-lg:mx-auto max-lg:w-1/2'
									: 'max-lg:w-full'
							]}
							src={headlineAsset.filename}
							alt={headlineAsset.alt}
							title={headlineAsset.title}
						/>
					{:else}
						<SbRichtext text={content.headline} />
					{/if}
				</div>
				{#if content.subheadline}
					<div
						class="mt-3 text-balance text-[20px]/[140%] tracking-[-.2px] lg:mt-5 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
					>
						<SbRichtext text={content.subheadline} />
					</div>
				{/if}
				<div
					class={[
						'mt-6 flex gap-x-2 lg:mt-9',
						{
							'max-lg:justify-center':
								['center-center', 'center-bottom'].includes(content.text_alignment_mobile) ||
								headlineAsset?.filename
						},
						{
							'lg:justify-center':
								['center-center', 'center-bottom'].includes(content.text_alignment_desktop) ||
								headlineAsset?.filename
						}
					]}
				>
					{#each content.buttons as btn}
						<Blok content={btn} />
					{/each}

					{#if fullscreenVideoPlaybackId}
						<Button class="btn-large btn-blur" onclick={() => (expandedVideo = true)}
							>{content.fullscreen_button_label}</Button
						>
					{/if}
				</div>
			</div>
			{#if content.press_banner?.length || 0 > 0}
				<div
					class="col-span-full mb-6 mt-2 rounded-xl bg-brown-6 px-5 py-4 lg:col-span-5 lg:col-start-2 lg:mt-15 lg:px-7 lg:py-6"
				>
					<p class="mb-4 text-[12px]/[140%] font-semibold uppercase tracking-[0.96px] text-brown-4">
						featured in
					</p>
					<div class="flex flex-wrap gap-x-2 gap-y-5 max-lg:justify-between lg:gap-x-8">
						{#each content.press_banner || [] as pressItem}
							<div class="h-4">
								<Blok content={pressItem} />
							</div>
						{/each}
					</div>
				</div>
			{/if}
		</PageGrid16>
	</div>
</section>

{#if expandedVideo}
	<FullscreenVideo
		backgroundWithOpacity
		buttonPos="top-right"
		playbackId={fullscreenVideoPlaybackId}
		onclose={() => (expandedVideo = false)}
	/>
{/if}

<style lang="scss">
	section {
		min-height: calc(100svh - var(--banner-height));
		transition-property: padding-top, height;
		transition-duration: 0.2s;

		@media (min-width: 1024px) {
			padding-top: var(--header-height);
		}
	}

	.desktop-header-margin {
		@media (min-width: 1024px) {
			margin-top: var(--header-height);
		}
	}

	.mobile-header-padding {
		@media (max-width: 1024px) {
			padding-top: var(--header-height);
		}
	}
</style>
